/**
 * Polish task 04 — TanStack Query bindings for /admin/groups. Groups are
 * managed through enies-lobby AuthzService admin RPCs (which front pangaea);
 * a group holds role grants exactly like a user, and members inherit them at
 * resolve time.
 */ import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { authzClient } from '../../../lib/connect';
export const GROUPS_KEY = 'admin:groups';
export const GROUP_MEMBERS_KEY = 'admin:group-members';
export const GROUP_ASSIGNMENTS_KEY = 'admin:group-assignments';
export function useListGroups(query, limit = 0, options = {}) {
    const { enabled = true } = options;
    return useQuery({
        queryKey: [
            GROUPS_KEY,
            query,
            limit
        ],
        enabled,
        placeholderData: keepPreviousData,
        queryFn: async ()=>(await authzClient.adminListGroups({
                query,
                limit
            })).groups
    });
}
export function useCreateGroup() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>authzClient.adminCreateGroup({
                key: args.key,
                displayName: args.displayName,
                description: args.description ?? ''
            }),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    GROUPS_KEY
                ]
            });
            toast.success('Group created');
        },
        onError: (e)=>toast.error(e instanceof Error ? e.message : 'Failed to create group')
    });
}
export function useUpdateGroup() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>authzClient.adminUpdateGroup({
                id: args.id,
                displayName: args.displayName,
                description: args.description ?? ''
            }),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    GROUPS_KEY
                ]
            });
            toast.success('Group updated');
        },
        onError: (e)=>toast.error(e instanceof Error ? e.message : 'Failed to update group')
    });
}
export function useDeleteGroup() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>authzClient.adminDeleteGroup({
                id: args.id
            }),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    GROUPS_KEY
                ]
            });
            toast.success('Group deleted');
        },
        // The server refuses (FailedPrecondition) while the group holds active
        // grants — surface that message rather than a generic failure.
        onError: (e)=>toast.error(e instanceof Error ? e.message : 'Failed to delete group')
    });
}
export function useGroupMembers(groupId, enabled = true) {
    return useQuery({
        queryKey: [
            GROUP_MEMBERS_KEY,
            groupId
        ],
        enabled: enabled && !!groupId,
        queryFn: async ()=>(await authzClient.adminListGroupMembers({
                groupId
            })).members
    });
}
export function useAddGroupMember() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>authzClient.adminAddGroupMember(args),
        onSuccess: (_r, vars)=>{
            qc.invalidateQueries({
                queryKey: [
                    GROUP_MEMBERS_KEY,
                    vars.groupId
                ]
            });
            qc.invalidateQueries({
                queryKey: [
                    GROUPS_KEY
                ]
            });
            toast.success('Member added');
        },
        onError: (e)=>toast.error(e instanceof Error ? e.message : 'Failed to add member')
    });
}
export function useRemoveGroupMember() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>authzClient.adminRemoveGroupMember(args),
        onSuccess: (_r, vars)=>{
            qc.invalidateQueries({
                queryKey: [
                    GROUP_MEMBERS_KEY,
                    vars.groupId
                ]
            });
            qc.invalidateQueries({
                queryKey: [
                    GROUPS_KEY
                ]
            });
            toast.success('Member removed');
        },
        onError: (e)=>toast.error(e instanceof Error ? e.message : 'Failed to remove member')
    });
}
export function useGroupAssignments(groupId, enabled = true) {
    return useQuery({
        queryKey: [
            GROUP_ASSIGNMENTS_KEY,
            groupId
        ],
        enabled: enabled && !!groupId,
        queryFn: async ()=>(await authzClient.adminListAssignmentsForGroup({
                groupId,
                includeInactive: false
            })).assignments
    });
}
